import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { storage, makeId } from "../lib/storage";
import { useAuth } from "./AuthContext";

const DataContext = createContext(null);

export function DataProvider({ children }) {
    const { scope, currentUser, isAdmin } = useAuth();

    // Source-of-truth (unscoped) state — kept in memory mirror of storage.
    const [allStudents, setAllStudents] = useState(() => storage.getStudents());
    const [allAttendance, setAllAttendance] = useState(() =>
        storage.getAttendance(),
    );

    // Persist on change.
    useEffect(() => {
        storage.saveStudents(allStudents);
    }, [allStudents]);
    useEffect(() => {
        storage.saveAttendance(allAttendance);
    }, [allAttendance]);

    const inScope = (rec) => scope === null || rec.teacherId === scope;
    const isLive = (s) => !s.archivedAt;

    const students = useMemo(
        () => allStudents.filter((s) => inScope(s) && isLive(s)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allStudents, scope],
    );
    const archivedStudents = useMemo(
        () => allStudents.filter((s) => inScope(s) && !isLive(s)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allStudents, scope],
    );
    const attendance = useMemo(
        () => allAttendance.filter(inScope),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allAttendance, scope],
    );

    function requireWorkspace(action) {
        if (scope === null) {
            toast.error(
                `Pick a teacher workspace to ${action} (admin "All workspaces" is read-only).`,
            );
            return false;
        }
        return true;
    }

    const api = useMemo(
        () => ({
            // Scoped read-only views
            students,
            archivedStudents,
            attendance,
            // Raw views (admin reports/teacher filter)
            allStudents,
            allAttendance,
            scope,
            isAdmin,

            addStudent(input) {
                if (!requireWorkspace("add a student")) return null;
                const now = new Date().toISOString();
                const student = {
                    id: makeId("stu"),
                    teacherId: scope,
                    createdAt: now,
                    ...input,
                };
                setAllStudents((prev) => [student, ...prev]);
                return student;
            },

            /**
             * Patch an existing student in place. Tenant-safe: refuses if the
             * student belongs to a different teacher's workspace.
             */
            updateStudent(id, patch) {
                const existing = allStudents.find((s) => s.id === id);
                if (!existing) return null;
                if (existing.teacherId !== scope) {
                    toast.error("Student belongs to another workspace.");
                    return null;
                }
                const now = new Date().toISOString();
                let saved = null;
                setAllStudents((prev) =>
                    prev.map((s) => {
                        if (s.id !== id) return s;
                        saved = {
                            ...s,
                            ...patch,
                            id: s.id,
                            teacherId: s.teacherId,
                            createdAt: s.createdAt,
                            updatedAt: now,
                        };
                        return saved;
                    }),
                );
                return saved;
            },

            /**
             * Soft-archive a student. Their attendance history is preserved;
             * they simply stop appearing in active lists (students, schedules,
             * dashboards). Reversible via restoreStudent.
             */
            archiveStudent(id) {
                const existing = allStudents.find((s) => s.id === id);
                if (!existing) return null;
                if (existing.teacherId !== scope) {
                    toast.error("Student belongs to another workspace.");
                    return null;
                }
                const now = new Date().toISOString();
                let saved = null;
                setAllStudents((prev) =>
                    prev.map((s) => {
                        if (s.id !== id) return s;
                        saved = { ...s, archivedAt: now, updatedAt: now };
                        return saved;
                    }),
                );
                return saved;
            },

            restoreStudent(id) {
                const existing = allStudents.find((s) => s.id === id);
                if (!existing) return null;
                if (existing.teacherId !== scope) {
                    toast.error("Student belongs to another workspace.");
                    return null;
                }
                const now = new Date().toISOString();
                let saved = null;
                setAllStudents((prev) =>
                    prev.map((s) => {
                        if (s.id !== id) return s;
                        const { archivedAt, ...rest } = s;
                        saved = { ...rest, updatedAt: now };
                        return saved;
                    }),
                );
                return saved;
            },

            /**
             * Admin-only. Reassigns a student AND every one of their attendance
             * records to a different teacher's workspace. Idempotent and atomic
             * relative to the React state batches.
             */
            transferStudent(id, toTeacherId) {
                if (!isAdmin) {
                    toast.error("Only an admin can transfer students.");
                    return null;
                }
                if (!toTeacherId) {
                    toast.error("Pick a destination teacher.");
                    return null;
                }
                const existing = allStudents.find((s) => s.id === id);
                if (!existing) return null;
                if (existing.teacherId === toTeacherId) {
                    toast.error("Student already belongs to that teacher.");
                    return null;
                }
                const now = new Date().toISOString();
                setAllStudents((prev) =>
                    prev.map((s) =>
                        s.id === id
                            ? { ...s, teacherId: toTeacherId, updatedAt: now }
                            : s,
                    ),
                );
                setAllAttendance((prev) =>
                    prev.map((r) =>
                        r.studentId === id ? { ...r, teacherId: toTeacherId } : r,
                    ),
                );
                return { ...existing, teacherId: toTeacherId, updatedAt: now };
            },

            getStudent(id) {
                return students.find((s) => s.id === id) || null;
            },
            /**
             * Includes archived students. Used by Student Detail when viewing
             * an archived record.
             */
            getAnyStudent(id) {
                return allStudents.find((s) => inScope(s) && s.id === id) || null;
            },

            saveAttendance({ studentId, date, status, remark }) {
                const student = allStudents.find((s) => s.id === studentId);
                if (!student) return null;
                if (!requireWorkspace("log attendance")) return null;
                // Writing for a student in another teacher's workspace requires
                // admin to switch to that workspace.
                if (student.teacherId !== scope) {
                    toast.error("Student belongs to another workspace.");
                    return null;
                }
                const now = new Date().toISOString();
                let saved = null;
                setAllAttendance((prev) => {
                    const existing = prev.find(
                        (r) => r.studentId === studentId && r.date === date,
                    );
                    if (existing) {
                        saved = {
                            ...existing,
                            status,
                            remark: remark ?? "",
                            studentName: student.name,
                            teacherId: student.teacherId,
                            updatedAt: now,
                        };
                        return prev.map((r) => (r.id === existing.id ? saved : r));
                    }
                    saved = {
                        id: makeId("att"),
                        teacherId: student.teacherId,
                        studentId,
                        studentName: student.name,
                        date,
                        status,
                        remark: remark ?? "",
                        createdAt: now,
                        updatedAt: now,
                    };
                    return [saved, ...prev];
                });
                return saved;
            },

            resolveMakeup(recordId) {
                const now = new Date();
                const label = `Done on ${now.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                })}`;
                setAllAttendance((prev) =>
                    prev.map((r) =>
                        r.id === recordId
                            ? { ...r, remark: label, updatedAt: now.toISOString() }
                            : r,
                    ),
                );
            },

            removeAttendance({ studentId, date }) {
                let removed = null;
                setAllAttendance((prev) => {
                    const target = prev.find(
                        (r) => r.studentId === studentId && r.date === date,
                    );
                    if (!target) return prev;
                    removed = target;
                    return prev.filter((r) => r.id !== target.id);
                });
                return removed;
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [students, archivedStudents, attendance, allStudents, allAttendance, scope, isAdmin, currentUser],
    );

    return <DataContext.Provider value={api}>{children}</DataContext.Provider>;
}

export function useData() {
    const ctx = useContext(DataContext);
    if (!ctx) throw new Error("useData must be used within DataProvider");
    return ctx;
}
